// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './Claim.scss';
import { useTranslation } from 'react-i18next';

import { Modal } from 'react-bootstrap';
import { format } from 'date-fns';
import { serverTimestamp, collection, doc, addDoc, deleteField, increment } from 'firebase/firestore';
import { debounce } from 'lodash';

import PropertyField from '../PropertyField/PropertyField';
import DeliveryScanner from '../DeliveryScanner/DeliveryScanner';
import BaleCard from '../BaleCard/BaleCard';
import ImageSquare from '../../shared_components/ImageSquare/ImageSquare';
import ClaimImageView from '../ClaimImageView/ClaimImageView';
import { firestore, storage } from '../../context/firebase';
import { uploadImage, deleteImageFromStorage } from '../../shared_components/utils/ImgUtils';

// Importing icons
import back_icon from '../../assets/icons/white/back_small.png';
import alert_icon from '../../assets/icons/white/alert.png';
import alert_icon_red from '../../assets/icons/red/alert.png';
import close_icon from '../../assets/icons/white/close.svg';
import notes_icon from '../../assets/icons/white/document.png';
import bell_icon from '../../assets/icons/white/bell.png';
import alert_on_icon from '../../assets/icons/red/alert.png';
import { toast } from 'sonner';
import { CLAIM_SETTINGS_DOCUMENT_PATH, MESSAGES_COLLECTION_PATH } from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import CommonModal from '../../shared_components/CommonModal/CommonModal';
import { useGetDocument } from '../../shared_components/hooks/useGetDocument';
import { useUpdateDocument } from '../../shared_components/hooks/useUpdateDocument';

export default function Claim({ delivery, handleDeliveryFieldChange, show, onHide, showBale, showDeliveryImage }) {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const currentUserUid = useAuthStore((state) => state.currentUser?.uid);
  const currentUserDisplayName = useAuthStore((state) => state.currentUser?.info?.displayName);
  const deliverySettings = useAuthStore((state) => state.deliverySettings);
  const claimSettings = useAuthStore((state) => state.claimSettings);
  const logEventCustom = useAuthStore((state) => state.logEventCustom);
  const suppliers = useAuthStore((state) => state.suppliers);
  const [showClaimCamera, setShowClaimCamera] = React.useState(false);
  const [showImage, setShowImage] = React.useState(null);
  const [showNotificationPopup, setShowNotificationPopup] = React.useState(false);
  const [claimNotes, setClaimNotes] = React.useState(delivery?.claim_data?.claim_notes ?? '');

  const handleClaimNotesChange = React.useCallback((e) => {
    setClaimNotes(e.target.value);
    debounce(() => handleDeliveryFieldChange({ target: { name: 'claim_notes', value: e.target.value } }), 700)();
  }, []);

  const claimSettingsRef = doc(firestore, CLAIM_SETTINGS_DOCUMENT_PATH(currentUserClientId));
  const { getDocument: getClaimSettings } = useGetDocument(claimSettingsRef, { initialCall: false });
  const { updateDocument: updateClaimSettings } = useUpdateDocument(claimSettingsRef);

  function handleClaimFieldChange(e) {
    handleDeliveryFieldChange({
      ...e,
      target: { name: 'claim_data', value: { [e.target.name]: e.target.value } },
    });
  }

  async function addClaimImage(image) {
    // Upload the image to storage and retrieve its URL
    const imageId = `${currentUserUid}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`;
    const imagePath = `clients/${currentUserClientId}/deliveries/reclamations/${delivery.id}/${imageId}.jpg`;
    const image_URL = await uploadImage(storage, image, imagePath);

    // Add the image url to the claim data
    handleClaimFieldChange({
      target: {
        name: 'claim_images',
        value: {
          [imageId]: {
            image_URL,
            image_storage_path: imagePath,
            timestamp: serverTimestamp(),
          },
        },
      },
    });

    // Log Event
    logEventCustom('add_claim_image', { delivery_id: delivery.id, image_id: imageId });
  }

  function scrollClaimImage(scrollAmount) {
    /* Change the displayed image by scrolling through scrollAmount images. */
    if (!showImage || !scrollAmount) return;

    const imageIds = Object.keys(delivery?.claim_data?.claim_images).sort();
    const currentIndex = imageIds.indexOf(showImage);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + scrollAmount;
    if (nextIndex >= imageIds.length || nextIndex < 0) return;
    const nextImageId = imageIds[nextIndex];
    setShowImage(nextImageId);
  }

  async function confirmClaim() {
    /* Confirm the claim and submit it to the database. */
    if (!delivery?.claim_data?.claim_nr) {
      const claimNr = await getClaimNr();
      handleDeliveryFieldChange({
        target: {
          name: 'claim_data',
          value: {
            claim_nr: claimNr,
            claim_status: 'confirmed',
            created_at: serverTimestamp(),
            created_by: currentUserUid,
            created_by_displayname: currentUserDisplayName,
          },
        },
      });
    }
    setShowNotificationPopup(true);

    // Log Event
    logEventCustom('confirm_claim', { delivery_id: delivery.id });
  }

  async function getClaimNr() {
    const claimSettingsUpdated = await getClaimSettings();
    let claimNr = 1;
    if (claimSettingsUpdated?.claims_counter) {
      claimNr = claimSettingsUpdated.claims_counter + 1;
    }
    await updateClaimSettings({ claims_counter: increment(1) });
    return claimNr;
  }

  async function handleNotification() {
    /* Trigger an email to recipients of the notification emails for claims. Will be processed by firebase add-on. */
    setShowNotificationPopup(false);
    for (const email of claimSettings.claim_notification_emails) {
      await addDoc(collection(firestore, MESSAGES_COLLECTION_PATH), {
        type: 'claim',
        date: serverTimestamp(),
        to: email,
        message: {
          subject: '[UpCircle] Neue Reklamation erfasst',
          html: `Es wurde eine neue Reklamation erfasst!<br>
                    Lieferant: <b>${suppliers?.[delivery.supplier_id]?.display_name ?? delivery.supplier_id}</b><br>
                    Datum: <b>${format(delivery.date, 'dd.MM.yyyy HH:mm')} Uhr</b><br>
                    Erfasst von: <b>${deliverySettings?.delivery_submitted_by_user?.[delivery.delivery_submitted_by_user] ?? delivery.delivery_submitted_by_user}</b><br>
                    Klicke auf den Link, um die Reklamation direkt im UpCircle Dashboard zu öffnen:<br>
                    <a href="https://analytics.upcircle.ai/deliveries/${delivery.id}">Öffne das Upcircle Dashboard</a><br><br>
                    Mit freundlichen Grüssen<br>
                    Dein UpCircle Team`,
        },
      });

      handleClaimFieldChange({ target: { name: 'notification_sent', value: true } });
    }
    toast.success(t('claim_send_confirmation'), { duration: 3000, dismissible: true });
    onHide();
  }

  async function deleteClaimImage(imageId) {
    /* Delete the image from storage and remove the image entry from the claim data */
    setShowImage(null);
    handleClaimFieldChange({
      target: {
        name: 'claim_images',
        value: {
          [imageId]: deleteField(),
        },
      },
    });
    await deleteImageFromStorage(storage, delivery.claim_data.claim_images[imageId].image_storage_path, [
      '_400x400',
      '_1920x1920',
    ]);

    // Log Event
    logEventCustom('delete_claim_image', { delivery_id: delivery.id, image_id: imageId });
  }

  function getScanner() {
    return (
      <div className='claims-scanner-container'>
        <DeliveryScanner
          addDeliveryImage={addClaimImage}
          viewLastDeliveryImage={() => {
            setShowClaimCamera(false);
            setShowImage(Object.keys(delivery?.claim_data?.claim_images).sort().pop());
          }}
          viewOverview={() => setShowClaimCamera(false)}
        />
        <button className='back-button' onClick={() => setShowClaimCamera(false)}>
          <img src={back_icon} />
        </button>
      </div>
    );
  }

  function getImageView() {
    return (
      <ClaimImageView
        claimImage={delivery?.claim_data?.claim_images[showImage]}
        onClose={() => setShowImage(null)}
        handleClaimImageChange={(e) =>
          handleClaimFieldChange({
            ...e,
            target: {
              name: 'claim_images',
              value: {
                [showImage]: { [e.target.name]: e.target.value },
              },
            },
          })
        }
        getNextImage={() => scrollClaimImage(1)}
        getPrevImage={() => scrollClaimImage(-1)}
        deleteImage={() => deleteClaimImage(showImage)}
      />
    );
  }

  function getClaimOverview() {
    return (
      <div className='claim-overview'>
        <div className='claim-header'>
          <h3 className='claim-title'>
            {t('claim')} {delivery?.claim_data?.claim_nr?.toString().padStart(4, '0')}
          </h3>
          <div className='close-button' onClick={onHide}>
            <img src={close_icon} />
          </div>
        </div>

        <div className='claim-properties'>
          <PropertyField
            icon={alert_icon}
            title={t('property_problems')}
            value={delivery?.claim_data?.delivery_issues ?? []}
            name='delivery_issues'
            type='multiple-choice-select'
            handleChange={handleClaimFieldChange}
            editable={true}
            options={Object.entries(claimSettings?.delivery_issues ?? {})
              .sort(([, objA], [, objB]) => {
                const sortIdA = objA.sort_id ?? 0;
                const sortIdB = objB.sort_id ?? 0;
                if (sortIdA !== sortIdB) {
                  return sortIdA - sortIdB;
                }
                return objA.display_name.localeCompare(objB.display_name);
              })
              .reduce((acc, [id, obj]) => {
                acc[id] = obj.display_name;
                return acc;
              }, {})}
          />
        </div>

        <div className='claim-bales-boxes'>
          <div className='claim-bales-box'>
            <h3 className='claim-bales-title'>{t('claim_photos')}</h3>
            <div className='claim-bales'>
              <div className='claim-bales-card-wrapper'>
                <div className='claim-bales-card-new' onClick={() => setShowClaimCamera(true)}>
                  <p>+</p>
                </div>
                {Object.entries(delivery?.claim_data?.claim_images ?? {})
                  .sort()
                  .map(([imageId, imageData]) => {
                    return (
                      <div onClick={() => setShowImage(imageId)} key={imageId}>
                        <ImageSquare
                          key={imageId}
                          storage={storage}
                          imageStoragePath={imageData.image_storage_path}
                          icons={imageData?.notes ? [notes_icon] : []}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className='claim-bales-box'>
            <h3 className='claim-bales-title'>{t('claim_linked_bales')}</h3>
            <div className='claim-bales'>
              <div className='claim-bales-card-wrapper'>
                {Object.entries(delivery.bales || {})
                  .filter(([, bale]) => bale?.flagged)
                  .sort()
                  .map(([baleId, bale]) => {
                    return (
                      <div
                        onClick={() => {
                          showBale(baleId);
                        }}
                        key={baleId}
                      >
                        <BaleCard
                          key={baleId}
                          imageStoragePath={bale?.image_storage_path}
                          flagged={bale.flagged}
                          baleId={baleId}
                          deliveryId={delivery.id}
                        />
                      </div>
                    );
                  })}
                {Object.entries(delivery.delivery_images || {})
                  .filter(([, image]) => image?.flagged)
                  .sort()
                  .map(([imageId, image]) => {
                    return (
                      <div
                        onClick={() => {
                          showDeliveryImage(imageId);
                        }}
                        key={imageId}
                      >
                        <ImageSquare
                          storage={storage}
                          imageStoragePath={image.image_storage_path}
                          icons={[alert_on_icon]}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className='claim-bales-box'>
            <h3 className='claim-bales-title'>{t('claim_notes')}</h3>
            <textarea
              className='claim-notes-textarea'
              placeholder={t('claim_notes_placeholder')}
              value={claimNotes}
              onChange={handleClaimNotesChange}
            />
          </div>
        </div>

        <div className='claim-footer-buttons'>
          <button className='claim-confirm-button' onClick={confirmClaim}>
            <img src={bell_icon} className='claim-confirm-icon' />
            {t('claim_confirm')}
          </button>
        </div>
        {/* Notify about claim Popup Modal */}
        <CommonModal
          show={showNotificationPopup}
          title={t('claim_send_confirm_title')}
          confirmText={t('claim_send')}
          cancelText={t('general_close')}
          onConfirm={handleNotification}
          onCancel={() => {
            setShowNotificationPopup(false);
            toast.success(t('claim_not_sent_confirmation'), { duration: 3000, dismissible: true });
            onHide();
          }}
          theme='dark'
          topCloseButton
        >
          {delivery?.claim_data?.notification_sent ? (
            <div className='flex items-center justify-center gap-0.5'>
              <img src={alert_icon_red} className='size-4' />
              <p className='text-red-400'>{t('claim_send_warning')}</p>
            </div>
          ) : null}
        </CommonModal>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} backdrop='static' className='modal'>
      <div
        className={`modal-box claim-modal-box ${showClaimCamera ? 'scanner' : showImage ? 'image-view' : 'overview'}`}
      >
        {showClaimCamera ? getScanner() : showImage ? getImageView() : getClaimOverview()}
      </div>
    </Modal>
  );
}
